/**
 * 類似日検索ダイアログ (SimilarDayDialog) フロー hook。
 *
 * ツールバー「類似日から選択」→ SearchSimilarDays 検索 → 初期選択解決
 * (`useSimilarDayInitialSelected`) → 「適用」で過去実績日付を確定、の一連の
 * state + 派生データを集約する。
 */
import { useCallback, useMemo, useState } from 'react'

import { create } from '@bufbuild/protobuf'
import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import {
  RecordSimilarDayPicksRequestSchema,
  type SimilarDayCandidate,
} from '@ebalance/proto/bffuser/v1/demand_forecast_service_pb'
import type { Date as ProtoDate } from '@ebalance/proto/google/type/date_pb'

import { parseIsoDate, toProtoDate } from '~/models/dateTime'
import { useRecordSimilarDayPicks } from '~/pages/DemandForecastDaily/hooks/useRecordSimilarDayPicks'
import { useSearchSimilarDays } from '~/pages/DemandForecastDaily/hooks/useSearchSimilarDays'
import { useSimilarDayInitialSelected } from '~/pages/DemandForecastDaily/hooks/useSimilarDayInitialSelected'

type Params = {
  caseId: string | undefined
  selectedAreaCode: AreaCode | undefined
  targetDate: ProtoDate | undefined
  /**
   * 既存の過去実績ヘッダー日付 (YYYY-MM-DD[]) の初期選択解決に使う。 取り直しの応答
   * (page.pastActuals) ではなく画面で選んでいる日付を渡す (#9296)。
   */
  existingHeaderDates: string[]
  /** 「適用」確定時、選択された日付群 (YYYY-MM-DD[]) を呼び出し元へ渡す */
  onApplyDates: (dates: string[]) => void
  /**
   * 自動選択件数 N (spec AC-019: 既定 3、上限 5、ホスト画面側から指定可能、design doc
   * AC-21)。ホスト画面 (= 本ダイアログの呼び出し元ページ) が明示指定しない場合は
   * 既定値 3 を使う。
   */
  autoSelectCount?: number
}

export type UseSimilarDayFlowResult = {
  isOpen: boolean
  open: () => void
  cancel: () => void
  lookbackDays: number
  onLookbackDaysChange: (days: number) => void
  candidates: SimilarDayCandidate[]
  isLoading: boolean
  /** 候補の取得に失敗した (再試行中は false) */
  isError: boolean
  /** 候補の取得をやり直す */
  retry: () => void
  initialSelected: string[]
  /** 「適用」で過去実績日付を確定しダイアログを閉じる */
  onApply: (dates: string[]) => void
}

/** google.type.Date → YYYY-MM-DD 文字列。 */
function protoDateToStr(d: ProtoDate | undefined): string | undefined {
  if (d === undefined) return undefined
  const y = d.year.toString().padStart(4, '0')
  const m = d.month.toString().padStart(2, '0')
  const day = d.day.toString().padStart(2, '0')
  return `${y}-${m}-${day}`
}

/** 類似日検索ダイアログの state + 派生データをまとめて返す。 */
export function useSimilarDayFlow({
  caseId,
  selectedAreaCode,
  targetDate,
  existingHeaderDates,
  onApplyDates,
  autoSelectCount = 3,
}: Params): UseSimilarDayFlowResult {
  const [isOpen, setIsOpen] = useState(false)
  const [lookbackDays, setLookbackDays] = useState(30)

  const {
    data: similarDayData,
    isLoading: isInitialLoading,
    isError: isQueryError,
    isFetching,
    refetch,
  } = useSearchSimilarDays({
    caseId,
    areaCode: selectedAreaCode,
    targetDate,
    autoSelectCount,
    lookbackDays,
    enabled: isOpen,
  })
  // 失敗後のやり直し中は query の status が error のまま (isLoading は false) なので、
  // 読込中として扱って失敗表示を下げる。
  // isLoading / isError は排他で、 dialog はこの 2 値をそのまま描画する。
  const isLoading = isInitialLoading || (isQueryError && isFetching)
  const isError = isQueryError && !isFetching
  // 失敗中は、 キャッシュ済みの前回結果 (古い候補・executionId) を一切使わない。
  const loadedData = isQueryError ? undefined : similarDayData
  const retry = useCallback((): void => {
    void refetch()
  }, [refetch])

  // 類似日ピック記録 (AC-14, ADR demand-forecast-similar-day-persistence-shape):
  // SearchSimilarDays 応答の execution_id を「検索結果が返ってきた時点」で保持し、
  // 「適用」実行時に RecordSimilarDayPicks へ渡す。fire-and-forget のため
  // mutateAsync の失敗は onApply 側で catch して無視する。
  const { mutateAsync: recordPicksAsync } = useRecordSimilarDayPicks()
  const executionId = loadedData?.executionId

  const autoSelected = useMemo<string[]>(() => {
    if (loadedData === undefined) return []
    return loadedData.candidates
      .filter((c) => c.autoSelected)
      .map((c) => protoDateToStr(c.date))
      .filter((s): s is string => s !== undefined)
  }, [loadedData])

  const { selected: initialSelected } = useSimilarDayInitialSelected({
    autoSelected,
    existingHeaderDates,
  })

  const open = useCallback((): void => {
    setIsOpen(true)
  }, [])

  const cancel = useCallback((): void => {
    setIsOpen(false)
  }, [])

  const onApply = useCallback(
    (dates: string[]): void => {
      // AC-14: 適用した日付群を RecordSimilarDayPicks へ記録する。fire-and-forget
      // (失敗しても適用操作自体はブロックしない、ADR
      // demand-forecast-similar-day-persistence-shape)。caseId / executionId が
      // 無い (= 検索を経由していない) 場合は記録をスキップする。
      if (
        caseId !== undefined &&
        executionId !== undefined &&
        executionId !== '' &&
        dates.length > 0
      ) {
        const pickedDates = dates
          .map((d) => parseIsoDate(d))
          .filter((d): d is Date => d !== undefined)
          .map(toProtoDate)
        recordPicksAsync(
          create(RecordSimilarDayPicksRequestSchema, {
            caseId,
            executionId,
            pickedDates,
          }),
        ).catch(() => {
          // fire-and-forget: 記録失敗を適用結果に反映させない (AC-14)
        })
      }
      onApplyDates(dates)
      setIsOpen(false)
    },
    [caseId, executionId, onApplyDates, recordPicksAsync],
  )

  return {
    isOpen,
    open,
    cancel,
    lookbackDays,
    onLookbackDaysChange: setLookbackDays,
    candidates: loadedData?.candidates ?? [],
    isLoading,
    isError,
    retry,
    initialSelected,
    onApply,
  }
}
